import React, { useEffect, useState, useMemo, useContext, useCallback } from "react";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  useTheme,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  alpha,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  DateRange as DateRangeIcon,
  BarChart as ChartIcon,
  CalendarToday as CalendarIcon,
  Diamond as DiamondIcon,
} from "@mui/icons-material";
import { DataContext } from "../../context/DataContext";
import { format, parseISO, startOfDay, endOfDay, isWithinInterval } from "date-fns";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const StockTransactionReport = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { loading: contextLoading, refresh, stock, companyDetails } = useContext(DataContext);
  // State for master data from company details
  const [itemCategories, setItemCategories] = useState([]);
  const [metalTypes, setMetalTypes] = useState([]);

  // Load master data from company details
  useEffect(() => {
    if (companyDetails && companyDetails.length > 0) {
      const company = companyDetails[0];

      // Load item categories
      if (company.items && Array.isArray(company.items)) {
        setItemCategories(company.items);
      }

      // Load metal types
      if (company.metals && Array.isArray(company.metals)) {
        setMetalTypes(company.metals);
      }
    }
  }, [companyDetails]);

  // Helper function to get item shortcut
  // const getItemShortcut = useCallback((itemName) => {
  //   if (!itemName) return '';
  //   const item = itemCategories.find(cat =>
  //     cat.name.toLowerCase() === itemName.toLowerCase()
  //   );
  //   return item ? item.shortcut : itemName.substring(0, 3).toUpperCase();
  // }, [itemCategories]);

  // Helper function to get metal shortcut
  // const getMetalShortcut = useCallback((metalName) => {
  //   if (!metalName) return '';
  //   const metal = metalTypes.find(m =>
  //     m.name.toLowerCase() === metalName.toLowerCase()
  //   );
  //   return metal ? metal.shortcut : metalName.substring(0, 2).toUpperCase();
  // }, [metalTypes]);

  // Helper function to parse sold_date format like "'16/1/2026"
  const parseSoldDate = (dateStr) => {
    if (!dateStr) return null;
    // Remove quotes and parse the date
    const cleanDateStr = dateStr.replace(/'/g, '');
    const [day, month, year] = cleanDateStr.split('/');
    // Create ISO string format for consistency
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`;
  };

  // Transform stock data to match expected format
  const transformedStockTransactions = useMemo(() => {
    if (!stock || stock.length === 0) return [];

    return stock.flatMap(item => {
      const transactions = [];

      // Create an "add" transaction for when the item was created
      if (item.createdAt) {
        transactions.push({
          id: `${item.id}-add`,
          type: "add",
          date: item.createdAt,
          productName: item.name,
          productCode: item.serialNo?.toString(),
          category: item.metal,
          subcategory: item.itemDetail || item.name,
          purity: `${item.melting}%`,
          weight: item.weight,
          unitPrice: item.price / (item.weight || 1),
          totalValue: item.price,
          metal: item.metal,
          melting: item.melting,
          wastage: item.wastage,
          makingCharges: item.makingCharges,
          goldCuttingRate: item.goldCuttingRate,
          supplierName: item.supplierName,
          supplierShortcut: item.supplierShortcut,
          huid: item.huid,
          originalItem: item
        });
      }

      // Create a "sold" transaction if the item was sold
      if (item.sold === "Yes" && item.soldDescription && item.soldDescription.sold_date) {
        const soldDate = parseSoldDate(item.soldDescription.sold_date);
        if (soldDate) {
          transactions.push({
            id: `${item.id}-sold`,
            type: "sold",
            date: soldDate,
            productName: item.name,
            productCode: item.serialNo?.toString(),
            category: item.metal,
            subcategory: item.itemDetail || item.name,
            purity: `${item.melting}%`,
            weight: item.weight,
            unitPrice: item.price / (item.weight || 1),
            totalValue: item.price,
            metal: item.metal,
            melting: item.melting,
            wastage: item.wastage,
            makingCharges: item.makingCharges,
            goldCuttingRate: item.goldCuttingRate,
            supplierName: item.supplierName,
            supplierShortcut: item.supplierShortcut,
            huid: item.huid,
            customer_id: item.soldDescription?.customer_id,
            sold_bill_no: item.soldDescription?.sold_bill_no,
            transaction_id: item.soldDescription?.transaction_id,
            originalItem: item
          });
        }
      }

      return transactions;
    });
  }, [stock]);

  // Use transformed data
  const stockTransactions = transformedStockTransactions;

  // State
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedSubcategory, setSelectedSubcategory] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const activeTab = 0;
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [stockAddedPage, setStockAddedPage] = useState(0);
  const [stockSoldPage, setStockSoldPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // Get unique categories from master data that exist in transactions
  const categories = useMemo(() => {
    const cats = new Set();
    stockTransactions?.forEach((t) => {
      if (t.category) cats.add(t.category);
    });
    return Array.from(cats);
  }, [stockTransactions]);

  // Get unique subcategories
  // const subcategories = useMemo(() => {
  //   const subs = new Set();
  //   stockTransactions?.forEach((t) => {
  //     if (t.subcategory) subs.add(t.subcategory);
  //   });
  //   return Array.from(subs);
  // }, [stockTransactions]);

  // FIXED: Calculate statistics from ALL transactions in date range (not filtered by type)
  const statistics = useMemo(() => {
    // First filter by date range only
    const dateFilteredTransactions = stockTransactions?.filter((transaction) => {
      if (!transaction?.date) return false;
      const transactionDate = parseISO(transaction.date);
      return isWithinInterval(transactionDate, {
        start: startOfDay(startDate),
        end: endOfDay(endDate),
      });
    }) || [];

    const stats = {
      totalAdded: 0,
      totalSold: 0,
      totalValueAdded: 0,
      totalValueSold: 0,
      totalWeightAdded: 0,
      totalWeightSold: 0,
      uniqueProducts: new Set(),
      categories: new Map(),
      subcategories: new Map(),
      topProducts: [],
      dailyAverages: {
        added: 0,
        sold: 0,
        value: 0,
        weight: 0,
      },
    };

    dateFilteredTransactions.forEach((transaction) => {
      stats.uniqueProducts.add(transaction.productCode || transaction.productName);

      // Update category stats
      if (transaction.category) {
        if (!stats.categories.has(transaction.category)) {
          stats.categories.set(transaction.category, { count: 0, value: 0 });
        }
        const cat = stats.categories.get(transaction.category);
        cat.count += 1;
        cat.value += transaction.totalValue || 0;
      }

      // Update subcategory stats
      if (transaction.subcategory) {
        if (!stats.subcategories.has(transaction.subcategory)) {
          stats.subcategories.set(transaction.subcategory, { count: 0, value: 0 });
        }
        const sub = stats.subcategories.get(transaction.subcategory);
        sub.count += 1;
        sub.value += transaction.totalValue || 0;
      }

      if (transaction.type === "add") {
        stats.totalAdded += 1;
        stats.totalValueAdded += transaction.totalValue || 0;
        stats.totalWeightAdded += transaction.weight || 0;
      } else if (transaction.type === "sold") {
        stats.totalSold += 1;
        stats.totalValueSold += transaction.totalValue || 0;
        stats.totalWeightSold += transaction.weight || 0;
      }
    });

    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) || 1;
    stats.dailyAverages.added = parseFloat((stats.totalAdded / daysDiff).toFixed(1));
    stats.dailyAverages.sold = parseFloat((stats.totalSold / daysDiff).toFixed(1));

    const totalValueAll = stats.totalValueAdded + stats.totalValueSold;
    const averageValuePerDay = totalValueAll / daysDiff;
    stats.dailyAverages.value = parseFloat((averageValuePerDay / 2).toFixed(2));

    const totalWeightAll = stats.totalWeightAdded + stats.totalWeightSold;
    stats.dailyAverages.weight = parseFloat((totalWeightAll / daysDiff).toFixed(2));

    // Find top 5 products by total value
    const productMap = new Map();
    dateFilteredTransactions.forEach((transaction) => {
      const productKey = transaction.productCode || transaction.productName;
      if (!productMap.has(productKey)) {
        productMap.set(productKey, {
          name: transaction.productName,
          code: transaction.productCode,
          category: transaction.category,
          subcategory: transaction.subcategory,
          purity: transaction.purity,
          totalAdded: 0,
          totalSold: 0,
          valueAdded: 0,
          valueSold: 0,
          weightAdded: 0,
          weightSold: 0,
        });
      }
      const product = productMap.get(productKey);
      if (transaction.type === "add") {
        product.totalAdded += 1;
        product.valueAdded += transaction.totalValue || 0;
        product.weightAdded += transaction.weight || 0;
      } else {
        product.totalSold += 1;
        product.valueSold += transaction.totalValue || 0;
        product.weightSold += transaction.weight || 0;
      }
    });

    stats.topProducts = Array.from(productMap.entries())
      .map(([key, product]) => ({
        ...product,
        netChange: product.totalAdded - product.totalSold,
        netValue: product.valueAdded - product.valueSold,
        netWeight: product.weightAdded - product.weightSold,
      }))
      .sort((a, b) => Math.abs(b.netValue) - Math.abs(a.netValue))
      .slice(0, 5);

    return stats;
  }, [stockTransactions, startDate, endDate]);

  // Filter transactions based on date range and filters (for table display only)
  const filteredTransactions = useMemo(() => {
    if (!stockTransactions || stockTransactions.length === 0) return [];

    return stockTransactions
      .filter((transaction) => {
        const transactionDate = parseISO(transaction.date);
        const isWithinDateRange = isWithinInterval(transactionDate, {
          start: startOfDay(startDate),
          end: endOfDay(endDate),
        });

        if (!isWithinDateRange) return false;
        if (selectedType !== "all" && transaction.type !== selectedType) return false;
        if (selectedCategory !== "all" && transaction.category !== selectedCategory) return false;
        if (selectedSubcategory !== "all" && transaction.subcategory !== selectedSubcategory) return false;

        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          return (
            transaction.productName?.toLowerCase().includes(searchLower) ||
            transaction.productCode?.toLowerCase().includes(searchLower) ||
            transaction.category?.toLowerCase().includes(searchLower) ||
            transaction.subcategory?.toLowerCase().includes(searchLower) ||
            transaction.purity?.toLowerCase().includes(searchLower)
          );
        }

        return true;
      })
      .sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case "date":
            aValue = parseISO(a.date).getTime();
            bValue = parseISO(b.date).getTime();
            break;
          case "value":
            aValue = a.totalValue;
            bValue = b.totalValue;
            break;
          case "weight":
            aValue = a.weight || 0;
            bValue = b.weight || 0;
            break;
          case "name":
            aValue = a.productName?.toLowerCase();
            bValue = b.productName?.toLowerCase();
            break;
          default:
            aValue = parseISO(a.date).getTime();
            bValue = parseISO(b.date).getTime();
        }

        if (sortOrder === "desc") {
          return bValue - aValue;
        }
        return aValue - bValue;
      });
  }, [stockTransactions, startDate, endDate, searchTerm, selectedType, selectedCategory, selectedSubcategory, sortBy, sortOrder]);

  // Separate added and sold transactions for tabs
  const addedTransactions = useMemo(() =>
    filteredTransactions.filter(t => t.type === "add"),
    [filteredTransactions]
  );

  const soldTransactions = useMemo(() =>
    filteredTransactions.filter(t => t.type === "sold"),
    [filteredTransactions]
  );

  // Get category color - updated to use master data
  const getCategoryColor = (category) => {
    const metal = metalTypes.find(m => m.name === category);
    const shortcut = metal ? metal.shortcut : '';

    switch (category) {
      case "Gold": return { bg: "#fbbf241a", color: "#f59e0b", darkBg: "#fbbf2433", icon: "💰", shortcut };
      case "Silver": return { bg: "#d1d5db1a", color: "#6b7280", darkBg: "#d1d5db33", icon: "⚪", shortcut };
      case "Diamond": return { bg: "#60a5fa1a", color: "#3b82f6", darkBg: "#60a5fa33", icon: "💎", shortcut };
      case "Platinum": return { bg: "#a5b4cb1a", color: "#64748b", darkBg: "#a5b4cb33", icon: "⚫", shortcut };
      default: return { bg: "#9ca3af1a", color: "#6b7280", darkBg: "#9ca3af33", icon: "✨", shortcut };
    }
  };

  // Get item category icon from master data
  const getItemIcon = (productName) => {
    const item = itemCategories.find(cat => cat.name === productName);
    if (item) {
      return item.shortcut;
    }
    return "💎";
  };

  // Get subcategory icon
  // const getSubcategoryIcon = (subcategory) => {
  //   switch (subcategory) {
  //     case "Necklace": return "📿";
  //     case "Ring": return "💍";
  //     case "Earrings": return "👂";
  //     case "Bracelet": return "🤲";
  //     case "Bangles": return "🌀";
  //     case "Chain": return "⛓️";
  //     case "Pendant": return "🔗";
  //     case "Nose Pin": return "👃";
  //     case "Anklet": return "🦶";
  //     case "Ladies": return "👩";
  //     case "Men": return "👨";
  //     case "Kids": return "👶";
  //     default: return "💎";
  //   }
  // };

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setIsLoading(true);
    try {
      await refresh();
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  }, [refresh]);

  // Toggle sort order
  const toggleSort = useCallback((field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  }, [sortBy, sortOrder]);

  // Reset filters
  const handleResetFilters = useCallback(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    setStartDate(thirtyDaysAgo);
    setEndDate(new Date());
    setSearchTerm("");
    setSelectedType("all");
    setSelectedCategory("all");
    setSelectedSubcategory("all");
    setSortBy("date");
    setSortOrder("desc");
    setStockAddedPage(0);
    setStockSoldPage(0);
  }, []);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Format weight
  const formatWeight = (weight) => {
    return `${parseFloat(weight).toFixed(2)} gm`;
  };

  // Handle pagination change
  const handleStockAddedPageChange = (event, newPage) => {
    setStockAddedPage(newPage);
  };

  const handleStockSoldPageChange = (event, newPage) => {
    setStockSoldPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setStockAddedPage(0);
    setStockSoldPage(0);
  };

  // Get paginated transactions
  const getPaginatedAddedTransactions = useMemo(() => {
    const startIndex = stockAddedPage * rowsPerPage;
    return addedTransactions.slice(startIndex, startIndex + rowsPerPage);
  }, [addedTransactions, stockAddedPage, rowsPerPage]);

  const getPaginatedSoldTransactions = useMemo(() => {
    const startIndex = stockSoldPage * rowsPerPage;
    return soldTransactions.slice(startIndex, startIndex + rowsPerPage);
  }, [soldTransactions, stockSoldPage, rowsPerPage]);

  // Table headers
  const tableHeaders = [
    { id: 'date', label: 'Date', align: 'left', sortable: true, minWidth: 120 },
    { id: 'productName', label: 'Product', align: 'left', sortable: true, minWidth: 200 },
    { id: 'productCode', label: 'Serial No.', align: 'left', sortable: false, minWidth: 100 },
    { id: 'weight', label: 'Weight (gm)', align: 'center', sortable: true, minWidth: 120 },
    { id: 'purity', label: 'Purity', align: 'center', sortable: false, minWidth: 100 },
    { id: 'category', label: 'Category', align: 'center', sortable: true, minWidth: 120 },
  ];

  // Loading state
  if (contextLoading || isLoading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={60} thickness={4} sx={{ color: "#d4af37" }} />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ py: 3, px: 2 }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 900,
                        background: isDark
                          ? 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)'
                          : 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        mb: 0.5,
                        ml: 3,
                      }}
                    >
                      Jewelry Stock Transactions
                    </Typography>
                    <Typography variant="body1" sx={{ color: isDark ? "#94a3b8" : "#64748b", ml: 3 }}>
                      Track jewelry stock additions and sales with detailed analytics
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", gap: 2, justifyContent: 'right' }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    sx={{
                      borderRadius: 2,
                      borderColor: isDark ? "#475569" : "#cbd5e1",
                      color: isDark ? "#e2e8f0" : "#475569",
                    }}
                  >
                    {isLoading ? "Refreshing..." : "Refresh"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Date Range Selection Card */}
          <Card sx={{ mb: 4, borderRadius: 3, bgcolor: "transparent" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: isDark ? "#e2e8f0" : "#1e293b" }}>
                <DateRangeIcon sx={{ mr: 1, verticalAlign: "middle", color: "#d4af37" }} />
                Select Date Range
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    maxDate={endDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon sx={{ color: "#d4af37" }} />
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    minDate={startDate}
                    maxDate={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon sx={{ color: "#d4af37" }} />
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", gap: 2, height: "100%", alignItems: "center" }}>
                    <Chip
                      label="Last 7 days"
                      onClick={() => {
                        const date = new Date();
                        date.setDate(date.getDate() - 7);
                        setStartDate(date);
                        setEndDate(new Date());
                      }}
                      sx={{
                        borderRadius: 2,
                        bgcolor: "#d4af371a",
                        color: "#d4af37",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    />
                    <Chip
                      label="Last 30 days"
                      onClick={() => {
                        const date = new Date();
                        date.setDate(date.getDate() - 30);
                        setStartDate(date);
                        setEndDate(new Date());
                      }}
                      sx={{
                        borderRadius: 2,
                        bgcolor: "#d4af371a",
                        color: "#d4af37",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    />
                    <Chip
                      label="Last 90 days"
                      onClick={() => {
                        const date = new Date();
                        date.setDate(date.getDate() - 90);
                        setStartDate(date);
                        setEndDate(new Date());
                      }}
                      sx={{
                        borderRadius: 2,
                        bgcolor: "#d4af371a",
                        color: "#d4af37",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleResetFilters}
                      sx={{
                        borderRadius: 2,
                        ml: "auto",
                        borderColor: isDark ? "#475569" : "#cbd5e1",
                        color: isDark ? "#94a3b8" : "#64748b",
                      }}
                    >
                      Reset Filters
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                height: "160px",
                position: "relative",
                overflow: "hidden",
                backgroundColor: isDark
                  ? "rgba(30, 41, 59, 0.4)"
                  : "rgba(255, 255, 255, 0.4)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(255, 255, 255, 0.3)"}`,
                borderRadius: "20px",
                boxShadow: isDark
                  ? "0 8px 32px rgba(0, 0, 0, 0.2)"
                  : "0 8px 32px rgba(212, 175, 55, 0.08)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: isDark
                    ? "0 12px 40px rgba(0, 0, 0, 0.25)"
                    : "0 12px 40px rgba(212, 175, 55, 0.12)",
                  backgroundColor: isDark
                    ? "rgba(30, 41, 59, 0.5)"
                    : "rgba(255, 255, 255, 0.7)",
                },
              }}>
                <Box sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "100%",
                  background: "linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, transparent 70%)",
                  opacity: isDark ? 0.15 : 0.1,
                }} />
                <CardContent sx={{
                  p: 3,
                  height: "100%",
                  width: '250px',
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  position: "relative",
                  zIndex: 1,
                }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Typography variant="caption" sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: isDark ? "rgba(255, 255, 255, 0.6)" : "rgba(30, 41, 59, 0.6)",
                    }}>
                      Total Transactions
                    </Typography>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(184, 134, 11, 0.1) 100%)",
                      backdropFilter: "blur(10px)",
                      border: `1px solid ${isDark ? "rgba(212, 175, 55, 0.3)" : "rgba(212, 175, 55, 0.2)"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                      <DiamondIcon sx={{ fontSize: 20, color: "#d4af37", opacity: 0.9 }} />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{
                      fontWeight: 800,
                      fontSize: "1.8rem",
                      lineHeight: 1,
                      mb: 0.5,
                      background: "linear-gradient(135deg, #d4af37 0%, #b8860b 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}>
                      {statistics.totalAdded + statistics.totalSold}
                    </Typography>
                  </Box>
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pt: 1.5,
                    borderTop: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(226, 232, 240, 0.5)"}`,
                  }}>
                    <Typography variant="caption" sx={{
                      fontSize: "0.7rem",
                      fontWeight: 500,
                      color: isDark ? "rgba(255, 255, 255, 0.5)" : "rgba(100, 116, 139, 0.7)",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}>
                      <CalendarIcon sx={{ fontSize: 12 }} />
                      {format(startDate, "dd MMM")} - {format(endDate, "dd MMM")}
                    </Typography>
                    <Box sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "#d4af37",
                      opacity: 0.8,
                      animation: "pulse 2s infinite",
                      "@keyframes pulse": {
                        "0%": { opacity: 0.8 },
                        "50%": { opacity: 0.4 },
                        "100%": { opacity: 0.8 },
                      }
                    }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                height: "160px",
                width: "250px",
                position: "relative",
                overflow: "hidden",
                backgroundColor: isDark
                  ? "rgba(30, 41, 59, 0.4)"
                  : "rgba(255, 255, 255, 0.4)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(255, 255, 255, 0.3)"}`,
                borderRadius: "20px",
                boxShadow: isDark
                  ? "0 8px 32px rgba(0, 0, 0, 0.2)"
                  : "0 8px 32px rgba(16, 185, 129, 0.08)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: isDark
                    ? "0 12px 40px rgba(0, 0, 0, 0.25)"
                    : "0 12px 40px rgba(16, 185, 129, 0.12)",
                  backgroundColor: isDark
                    ? "rgba(30, 41, 59, 0.5)"
                    : "rgba(255, 255, 255, 0.7)",
                },
              }}>
                <Box sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "100%",
                  background: "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, transparent 70%)",
                  opacity: isDark ? 0.15 : 0.1,
                }} />
                <CardContent sx={{
                  p: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  position: "relative",
                  zIndex: 1,
                }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Typography variant="caption" sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: isDark ? "rgba(255, 255, 255, 0.6)" : "rgba(30, 41, 59, 0.6)",
                    }}>
                      Items Added
                    </Typography>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.1) 100%)",
                      backdropFilter: "blur(10px)",
                      border: `1px solid ${isDark ? "rgba(16, 185, 129, 0.3)" : "rgba(16, 185, 129, 0.2)"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                      <TrendingUpIcon sx={{ fontSize: 20, color: "#10b981", opacity: 0.9 }} />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{
                      fontWeight: 800,
                      fontSize: "1.8rem",
                      lineHeight: 1,
                      mb: 0.5,
                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}>
                      {statistics.totalAdded}
                    </Typography>
                    <Typography variant="caption" sx={{
                      fontSize: "0.7rem",
                      fontWeight: 500,
                      color: "#10b981",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}>
                      <AddIcon sx={{ fontSize: 12 }} />
                      Weight: {formatWeight(statistics.totalWeightAdded)}
                    </Typography>
                  </Box>
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pt: 1.5,
                    borderTop: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(226, 232, 240, 0.5)"}`,
                  }}>
                    <Typography variant="caption" sx={{
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      color: "#10b981",
                      background: "rgba(16, 185, 129, 0.1)",
                      px: 1,
                      py: 0.25,
                      borderRadius: "4px",
                    }}>
                      +{statistics.totalAdded} items
                    </Typography>
                    <Box sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "#10b981",
                      opacity: 0.8,
                      animation: "pulse 2s infinite",
                    }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                height: "160px",
                width: "250px",
                position: "relative",
                overflow: "hidden",
                backgroundColor: isDark
                  ? "rgba(30, 41, 59, 0.4)"
                  : "rgba(255, 255, 255, 0.4)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(255, 255, 255, 0.3)"}`,
                borderRadius: "20px",
                boxShadow: isDark
                  ? "0 8px 32px rgba(0, 0, 0, 0.2)"
                  : "0 8px 32px rgba(239, 68, 68, 0.08)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: isDark
                    ? "0 12px 40px rgba(0, 0, 0, 0.25)"
                    : "0 12px 40px rgba(239, 68, 68, 0.12)",
                  backgroundColor: isDark
                    ? "rgba(30, 41, 59, 0.5)"
                    : "rgba(255, 255, 255, 0.7)",
                },
              }}>
                <Box sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "100%",
                  background: "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, transparent 70%)",
                  opacity: isDark ? 0.15 : 0.1,
                }} />
                <CardContent sx={{
                  p: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  position: "relative",
                  zIndex: 1,
                }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Typography variant="caption" sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: isDark ? "rgba(255, 255, 255, 0.6)" : "rgba(30, 41, 59, 0.6)",
                    }}>
                      Items Sold
                    </Typography>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.1) 100%)",
                      backdropFilter: "blur(10px)",
                      border: `1px solid ${isDark ? "rgba(239, 68, 68, 0.3)" : "rgba(239, 68, 68, 0.2)"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                      <TrendingDownIcon sx={{ fontSize: 20, color: "#ef4444", opacity: 0.9 }} />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{
                      fontWeight: 800,
                      fontSize: "1.8rem",
                      lineHeight: 1,
                      mb: 0.5,
                      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}>
                      {statistics.totalSold}
                    </Typography>
                    <Typography variant="caption" sx={{
                      fontSize: "0.7rem",
                      fontWeight: 500,
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}>
                      <RemoveIcon sx={{ fontSize: 12 }} />
                      Weight: {formatWeight(statistics.totalWeightSold)}
                    </Typography>
                  </Box>
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pt: 1.5,
                    borderTop: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(226, 232, 240, 0.5)"}`,
                  }}>
                    <Typography variant="caption" sx={{
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      color: "#ef4444",
                      background: "rgba(239, 68, 68, 0.1)",
                      px: 1,
                      py: 0.25,
                      borderRadius: "4px",
                    }}>
                      -{statistics.totalSold} items
                    </Typography>
                    <Box sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "#ef4444",
                      opacity: 0.8,
                      animation: "pulse 2s infinite",
                    }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                height: "160px",
                width: "250px",
                position: "relative",
                overflow: "hidden",
                backgroundColor: isDark
                  ? "rgba(30, 41, 59, 0.4)"
                  : "rgba(255, 255, 255, 0.4)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(255, 255, 255, 0.3)"}`,
                borderRadius: "20px",
                boxShadow: isDark
                  ? "0 8px 32px rgba(0, 0, 0, 0.2)"
                  : "0 8px 32px rgba(139, 92, 246, 0.08)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: isDark 
                    ? "0 12px 40px rgba(0, 0, 0, 0.25)"
                    : "0 12px 40px rgba(139, 92, 246, 0.12)",
                  backgroundColor: isDark
                    ? "rgba(30, 41, 59, 0.5)"
                    : "rgba(255, 255, 255, 0.7)",
                },
              }}>
                <Box sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "100%",
                  background: "linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, transparent 70%)",
                  opacity: isDark ? 0.15 : 0.1,
                }} />
                <CardContent sx={{ 
                  p: 3, 
                  height: "100%", 
                  display: "flex", 
                  flexDirection: "column",
                  justifyContent: "space-between",
                  position: "relative",
                  zIndex: 1,
                }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Typography variant="caption" sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: isDark ? "rgba(255, 255, 255, 0.6)" : "rgba(30, 41, 59, 0.6)",
                    }}>
                      Total Value
                    </Typography>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(124, 58, 237, 0.1) 100%)",
                      backdropFilter: "blur(10px)",
                      border: `1px solid ${isDark ? "rgba(139, 92, 246, 0.3)" : "rgba(139, 92, 246, 0.2)"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                      <ChartIcon sx={{ fontSize: 20, color: "#8b5cf6", opacity: 0.9 }} />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{
                      fontWeight: 800,
                      fontSize: "1.8rem",
                      lineHeight: 1,
                      mb: 0.5,
                      background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}>
                      {formatCurrency(statistics.totalValueAdded + statistics.totalValueSold)}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "space-between",
                    pt: 1.5,
                    borderTop: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(226, 232, 240, 0.5)"}`,
                  }}>
                    <Typography variant="caption" sx={{
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      color: "#8b5cf6",
                      background: "rgba(139, 92, 246, 0.1)",
                      px: 1,
                      py: 0.25,
                      borderRadius: "4px",
                    }}>
                      Daily avg: {formatCurrency(statistics.dailyAverages.value)}
                    </Typography>
                    <Box sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "#8b5cf6",
                      opacity: 0.8,
                      animation: "pulse 2s infinite",
                    }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Filters and Search */}
          <Card sx={{ mb: 4, borderRadius: 3, bgcolor: "transparent" }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    placeholder="Search jewelry..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#d4af37" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} label="Category">
                      <MenuItem value="all">All Categories</MenuItem>
                      {categories.map((cat) => {
                        const metal = metalTypes.find(m => m.name === cat);
                        const shortcut = metal ? metal.shortcut : '';
                        return (
                          <MenuItem key={cat} value={cat}>
                            {cat} {shortcut ? `(${shortcut})` : ''}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Main Content */}
          {activeTab === 0 && (
            <Paper
              sx={{
                borderRadius: "20px",
                background: isDark
                  ? "rgba(30, 41, 59, 0.8)"
                  : "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(20px)",
                border: `1px solid ${alpha(
                  isDark ? "#475569" : "#e2e8f0",
                  isDark ? 0.2 : 0.3
                )}`,
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
                mb: 3,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: alpha(
                    isDark ? "#475569" : "#e2e8f0",
                    isDark ? 0.2 : 0.3
                  ),
                  backgroundColor: isDark
                    ? "rgba(30, 41, 59, 0.5)"
                    : "rgba(248, 250, 252, 0.5)",
                }}
              >
                <Tabs
                  value={selectedType === "all" ? 0 : selectedType === "add" ? 0 : 1}
                  onChange={(e, newValue) => setSelectedType(newValue === 0 ? "add" : "sold")}
                  variant="fullWidth"
                  sx={{
                    "& .MuiTab-root": {
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "1rem",
                      minHeight: 60,
                      color: isDark ? "#94a3b8" : "#64748b",
                      "&.Mui-selected": {
                        color: isDark ? "#60a5fa" : "#2563eb",
                      },
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: isDark ? "#60a5fa" : "#2563eb",
                      height: 3,
                      borderRadius: "3px",
                    },
                  }}
                >
                  <Tab label={`Stock Added (${statistics?.totalAdded})`} />
                  <Tab label={`Stock Sold (${statistics?.totalSold})`} />
                </Tabs>
              </Box>

              <Box>
                <Box
                  sx={{
                    p: 3,
                    borderBottom: `1px solid ${alpha(
                      isDark ? "#475569" : "#e2e8f0",
                      isDark ? 0.2 : 0.3
                    )}`,
                    backgroundColor:
                      selectedType === "add" || selectedType === "all"
                        ? isDark
                          ? "rgba(16, 185, 129, 0.05)"
                          : "rgba(240, 253, 244, 0.5)"
                        : isDark
                          ? "rgba(239, 68, 68, 0.05)"
                          : "rgba(254, 242, 242, 0.5)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: isDark ? "#f1f5f9" : "#1e293b",
                        fontWeight: 700,
                      }}
                    >
                      {selectedType === "add" || selectedType === "all"
                        ? "Stock Added Transactions"
                        : "Stock Sold Transactions"}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: selectedType === "add" || selectedType === "all" ? "#10b981" : "#ef4444",
                      }}
                    >
                      {selectedType === "add" || selectedType === "all"
                        ? `Total: ${addedTransactions.length} items`
                        : `Total: ${soldTransactions.length} items`}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <TableContainer
                    sx={{
                      maxHeight: 500,
                      width: "100%",
                      overflow: "auto",
                      "&::-webkit-scrollbar": {
                        height: 10,
                        width: 10,
                      },
                      "&::-webkit-scrollbar-track": {
                        background: isDark
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.05)",
                        borderRadius: 6,
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: isDark
                          ? "rgba(255, 255, 255, 0.2)"
                          : "rgba(0, 0, 0, 0.2)",
                        borderRadius: 6,
                        "&:hover": {
                          background: isDark
                            ? "rgba(255, 255, 255, 0.3)"
                            : "rgba(0, 0, 0, 0.3)",
                        },
                      },
                    }}
                  >
                    <Table
                      stickyHeader
                      sx={{
                        width: "100%",
                        tableLayout: "auto",
                        minWidth: "100%",
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          {tableHeaders.map((header) => (
                            <TableCell
                              key={header.id}
                              align={header.align || "left"}
                              sx={{
                                backgroundColor: isDark
                                  ? "rgba(30, 41, 59, 0.9)"
                                  : "rgba(255, 255, 255, 0.95)",
                                color: isDark ? "#94a3b8" : "#64748b",
                                fontWeight: 700,
                                fontSize: "0.875rem",
                                borderBottom: `2px solid ${alpha(
                                  isDark ? "#475569" : "#e2e8f0",
                                  isDark ? 0.3 : 0.4
                                )}`,
                                whiteSpace: "nowrap",
                                padding: "16px 12px",
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                                minWidth: header?.minWidth,
                                cursor: header.sortable ? "pointer" : "default",
                                "&:hover": header.sortable
                                  ? {
                                    backgroundColor: isDark
                                      ? "rgba(96, 165, 250, 0.1)"
                                      : "rgba(37, 99, 235, 0.05)",
                                  }
                                  : {},
                                userSelect: "none",
                              }}
                              onClick={header.sortable ? () => toggleSort(header.id) : undefined}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent:
                                    header.align === "right"
                                      ? "flex-end"
                                      : header.align === "center"
                                        ? "center"
                                        : "flex-start",
                                  gap: 1,
                                  width: "100%",
                                }}
                              >
                                {header.sortable ? (
                                  <TableSortLabel
                                    active={sortBy === header.id}
                                    direction={sortBy === header.id ? sortOrder : "asc"}
                                    hideSortIcon={false}
                                    sx={{
                                      "& .MuiTableSortLabel-icon": {
                                        color: isDark ? "#60a5fa" : "#2563eb",
                                      },
                                      flexGrow: 1,
                                    }}
                                  >
                                    {header.label}
                                  </TableSortLabel>
                                ) : (
                                  <Box sx={{ flexGrow: 1 }}>{header.label}</Box>
                                )}
                              </Box>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(selectedType === "all" || selectedType === "add"
                          ? getPaginatedAddedTransactions
                          : getPaginatedSoldTransactions
                        ).length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={tableHeaders.length}
                              align="center"
                              sx={{
                                py: 6,
                                color: isDark ? "#94a3b8" : "#64748b",
                                border: "none",
                              }}
                            >
                              <Box sx={{ py: 4 }}>
                                <DiamondIcon
                                  sx={{
                                    fontSize: 64,
                                    color: isDark ? "#475569" : "#e2e8f0",
                                    mb: 2,
                                    opacity: 0.5,
                                  }}
                                />
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                  No transactions found
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    mb: 3,
                                    color: isDark ? "#64748b" : "#94a3b8",
                                  }}
                                >
                                  {selectedType === "add" || selectedType === "all"
                                    ? "No stock added transactions for this date"
                                    : "No stock sold transactions for this date"}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : (
                          (selectedType === "all" || selectedType === "add"
                            ? getPaginatedAddedTransactions
                            : getPaginatedSoldTransactions
                          ).map((transaction) => {
                            const categoryColors = getCategoryColor(transaction.category);
                            const itemIcon = getItemIcon(transaction.productName);

                            return (
                              <TableRow
                                key={transaction.id}
                                hover
                                sx={{
                                  "&:hover": {
                                    backgroundColor: isDark
                                      ? "rgba(255, 255, 255, 0.05)"
                                      : "rgba(0, 0, 0, 0.02)",
                                    transform: "translateY(-2px)",
                                    boxShadow: isDark
                                      ? "0 4px 12px rgba(0, 0, 0, 0.1)"
                                      : "0 4px 12px rgba(0, 0, 0, 0.05)",
                                    transition: "all 0.2s ease",
                                  },
                                  transition: "all 0.2s ease",
                                  position: "relative",
                                  "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: 1,
                                    background: isDark
                                      ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)"
                                      : "linear-gradient(90deg, transparent, rgba(0,0,0,0.03), transparent)",
                                  },
                                }}
                              >
                                {/* Date */}
                                <TableCell
                                  sx={{
                                    padding: "12px 16px",
                                    minWidth: tableHeaders[0].minWidth,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 700,
                                        color: isDark ? "#e2e8f0" : "#1e293b",
                                      }}
                                    >
                                      {format(parseISO(transaction.date), "dd MMM")}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: isDark ? "#94a3b8" : "#64748b",
                                        display: "block",
                                        mt: 0.5,
                                      }}
                                    >
                                      {transaction.type === "add" && format(parseISO(transaction.date), "hh:mm a")}
                                    </Typography>
                                  </Box>
                                </TableCell>

                                {/* Product Name */}
                                <TableCell
                                  sx={{
                                    padding: "12px 16px",
                                    minWidth: tableHeaders[1].minWidth,
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography sx={{ fontSize: "1.2rem" }}>
                                      {itemIcon}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: isDark ? "#e2e8f0" : "#1e293b",
                                      }}
                                    >
                                      {transaction.productName}
                                      {transaction.subcategory !== transaction.productName && (
                                        <Typography
                                          component="span"
                                          variant="caption"
                                          sx={{
                                            color: isDark ? "#94a3b8" : "#64748b",
                                            ml: 0.5,
                                            display: "block",
                                            fontSize: "0.7rem",
                                          }}
                                        >
                                          {transaction.subcategory}
                                        </Typography>
                                      )}
                                    </Typography>
                                  </Box>
                               </TableCell>

                                {/* Product Code */}
                                <TableCell
                                  sx={{
                                    padding: "12px 16px",
                                    minWidth: tableHeaders[2].minWidth,
                                  }}
                                >
                                  <Chip
                                    label={transaction.productCode}
                                    size="small"
                                    sx={{
                                      backgroundColor: isDark
                                        ? "rgba(255, 255, 255, 0.1)"
                                        : "rgba(0, 0, 0, 0.05)",
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontWeight: 600,
                                      fontSize: "0.75rem",
                                      height: 24,
                                    }}
                                  />
                                </TableCell>

                                {/* Weight */}
                                <TableCell
                                  align="center"
                                  sx={{
                                    padding: "12px 16px",
                                    minWidth: tableHeaders[3].minWidth,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: isDark ? "#e2e8f0" : "#1e293b",
                                    }}
                                  >
                                    {formatWeight(transaction.weight)}
                                  </Typography>
                                </TableCell>

                                {/* Purity */}
                                <TableCell
                                  align="center"
                                  sx={{
                                    padding: "12px 16px",
                                    minWidth: tableHeaders[4].minWidth,
                                  }}
                                >
                                  <Chip
                                    label={transaction.purity}
                                    size="small"
                                    sx={{
                                      backgroundColor: categoryColors.bg,
                                      color: categoryColors.color,
                                      border: `1px solid ${alpha(categoryColors.color, 0.3)}`,
                                      fontWeight: 600,
                                      fontSize: "0.75rem",
                                    }}
                                  />
                                </TableCell>

                                {/* Category */}
                                <TableCell
                                  align="center"
                                  sx={{
                                    padding: "12px 16px",
                                    minWidth: tableHeaders[5].minWidth,
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                                    <Typography sx={{ fontSize: "1rem" }}>
                                      {categoryColors.icon}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: categoryColors.color,
                                      }}
                                    >
                                      {transaction.category}
                                      {categoryColors.shortcut && ` (${categoryColors.shortcut})`}
                                    </Typography>
                                  </Box>
                                </TableCell>

                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <TablePagination
                  rowsPerPageOptions={[15, 25, 50]}
                  component="div"
                  count={
                    selectedType === "all" || selectedType === "add"
                      ? addedTransactions.length
                      : soldTransactions.length
                  }
                  rowsPerPage={rowsPerPage}
                  page={
                    selectedType === "all" || selectedType === "add"
                      ? stockAddedPage
                      : stockSoldPage
                  }
                  onPageChange={
                    selectedType === "all" || selectedType === "add"
                      ? handleStockAddedPageChange
                      : handleStockSoldPageChange
                  }
                  onRowsPerPageChange={handleRowsPerPageChange}
                  sx={{
                    backgroundColor: isDark
                      ? "rgba(30, 41, 59, 0.5)"
                      : "rgba(248, 250, 252, 0.5)",
                    borderTop: `1px solid ${alpha(
                      isDark ? "#475569" : "#e2e8f0",
                      isDark ? 0.2 : 0.3
                    )}`,
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                    {
                      color: isDark ? "#94a3b8" : "#64748b",
                    },
                  }}
                />
              </Box>
            </Paper>
          )}

          {activeTab === 1 && (
            <Grid container spacing={3}>
              {/* Summary Card */}
              <Grid item xs={12} md={8}>
                <Card sx={{ borderRadius: 3, bgcolor:"transparent", height: "100%" }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: isDark ? "#e2e8f0" : "#1e293b" }}>
                      <ChartIcon sx={{ mr: 1, verticalAlign: "middle", color: "#d4af37" }} />
                      Transaction Summary
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 3, borderRadius: 2, bgcolor: "#10b9811a", border: "1px solid #10b98133" }}>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: "#10b981", mb: 1 }}>
                            {statistics.totalAdded}
                          </Typography>
                          <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b", mb: 2 }}>
                            Items Added
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#10b981", fontWeight: 600, mb: 1 }}>
                            Weight: {formatWeight(statistics.totalWeightAdded)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#10b981", fontWeight: 600 }}>
                            Value: {formatCurrency(statistics.totalValueAdded)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 3, borderRadius: 2, bgcolor: "#ef44441a", border: "1px solid #ef444433" }}>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: "#ef4444", mb: 1 }}>
                            {statistics.totalSold}
                          </Typography>
                          <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b", mb: 2 }}>
                            Items Sold
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#ef4444", fontWeight: 600, mb: 1 }}>
                            Weight: {formatWeight(statistics.totalWeightSold)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#ef4444", fontWeight: 600 }}>
                            Value: {formatCurrency(statistics.totalValueSold)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12}>
                        <Paper sx={{ p: 3, borderRadius: 2, bgcolor: "#3b82f61a", border: "1px solid #3b82f633" }}>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: "#3b82f6", mb: 1 }}>
                            {formatCurrency(statistics.totalValueAdded + statistics.totalValueSold)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b", mb: 2 }}>
                            Total Transaction Value
                          </Typography>
                          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                            {Array.from(statistics.categories.entries())?.map(([category, data]) => {
                              const catColor = getCategoryColor(category);
                              return (
                                <Box key={category} sx={{ flex: "1 1 200px", minWidth: 200 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <Typography sx={{ fontSize: "1.2rem" }}>{catColor.icon}</Typography>
                                    <Typography variant="body2" sx={{ color: catColor.color, fontWeight: 600 }}>
                                      {category} {catColor.shortcut && `(${catColor.shortcut})`}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", display: "block" }}>
                                    {data.count} transactions • {formatCurrency(data.value)}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {activeTab === 2 && (
            <Card sx={{ borderRadius: 3, bgcolor: "transparent" }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: isDark ? "#e2e8f0" : "#1e293b" }}>
                  <TrendingUpIcon sx={{ mr: 1, verticalAlign: "middle", color: "#d4af37" }} />
                  Top Jewellry Products
                </Typography>
                <Grid container spacing={3}>
                  {statistics.topProducts.map((product, index) => {
                    const isPositive = product.netChange >= 0;
                    const color = isPositive ? "#10b981" : "#ef4444";
                    const catColor = getCategoryColor(product.category);
                    const itemIcon = getItemIcon(product.name);
                    return (
                      <Grid item xs={12} md={6} key={index}>
                        <Paper
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            bgcolor: "transparent",
                            border: "1px solid " + (isPositive ? "#10b98133" : "#ef444433"),
                            position: "relative",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              right: 0,
                              width: 80,
                              height: 80,
                              background: "linear-gradient(135deg, " + (isPositive ? "#10b9811a" : "#ef44441a") + " 0%, transparent 100%)",
                              borderRadius: "0 0 0 80px",
                            }}
                          />
                          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: isPositive ? "#10b9811a" : "#ef44441a",
                                border: "1px solid " + (isPositive ? "#10b98133" : "#ef444433"),
                                mr: 2,
                              }}
                            >
                              <Typography sx={{ fontSize: "1.2rem" }}>{itemIcon}</Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b" }}>
                                {product.name}
                              </Typography>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                <Chip
                                  label={product.category}
                                  size="small"
                                  sx={{ bgcolor: catColor.bg, color: catColor.color, fontWeight: 600, fontSize: "0.6rem", height: 20 }}
                                />
                                {product.purity && (
                                  <Chip
                                    label={product.purity}
                                    size="small"
                                    sx={{ bgcolor: "#6b72801a", color: "#6b7280", fontWeight: 600, fontSize: "0.6rem", height: 20 }}
                                  />
                                )}
                              </Box>
                            </Box>
                            <Chip label={"Rank #" + (index + 1)} size="small" sx={{ bgcolor: "#d4af371a", color: "#d4af37", fontWeight: 600, borderRadius: 2 }} />
                          </Box>
                          <Grid container spacing={2}>
                            <Grid item xs={3}>
                              <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.75rem" }}>
                                Added
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: "#10b981", fontSize: "1rem" }}>
                                {product.totalAdded}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.75rem" }}>
                                Sold
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: "#ef4444", fontSize: "1rem" }}>
                                {product.totalSold}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.75rem" }}>
                                Net
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: color, fontSize: "1rem" }}>
                                {isPositive ? "+" : ""}{product.netChange}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.75rem" }}>
                                Value
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: "#d4af37", fontSize: "0.9rem" }}>
                                {formatCurrency(product.netValue)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          )}

        </Container>
      </Box>
    </LocalizationProvider>
  );
};

export default React.memo(StockTransactionReport);